import React, { useState, useEffect, useRef } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

interface Step {
  target: string;
  title: string;
  content: string;
  placement?: "top" | "bottom" | "left" | "right";
}

interface OnboardingGuideProps {
  run?: boolean;
  onComplete?: () => void;
  manualTrigger?: boolean;
}

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({
  run = false,
  onComplete,
  manualTrigger = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);

  const steps: Step[] = [
    {
      target: "body",
      title: "Welcome to AI Recruiter! 🎉",
      content:
        "Let's take a quick tour to help you get started with our powerful recruitment platform.",
      placement: "bottom",
    },
    {
      target: '[data-tour="create-job"]',
      title: "Create Your First Job",
      content:
        "Click here to create a new job posting. You can define job details, interview questions, and assessment criteria.",
      placement: "bottom",
    },
    {
      target: '[data-tour="jobs-dashboard"]',
      title: "View All Jobs",
      content:
        "Access your job dashboard to view, edit, and manage all your job postings in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="candidates"]',
      title: "Manage Candidates",
      content:
        "View and manage all candidates who have applied for your positions. Track their progress and review their assessments.",
      placement: "bottom",
    },
    {
      target: '[data-tour="reports"]',
      title: "Access Reports",
      content:
        "View detailed candidate evaluation reports with AI-powered insights, scores, and recommendations.",
      placement: "bottom",
    },
    {
      target: "body",
      title: "You're All Set! 🚀",
      content:
        "You now know the basics of AI Recruiter. Start by creating your first job posting! You can restart this tour anytime from the help menu.",
      placement: "bottom",
    },
  ];

  useEffect(() => {
    // Check if this is a manual trigger or first login
    if (manualTrigger) {
      setTimeout(() => setIsActive(true), 500);
      return;
    }

    // Check for first login
    const loginData = localStorage.getItem("loginData");
    const hasCompletedOnboarding = localStorage.getItem("onboarding_completed");

    if (loginData && !hasCompletedOnboarding && run) {
      try {
        const parsedData = JSON.parse(loginData);
        const isFirstLogin = parsedData.data?.admin?.isFirstLogin;

        if (isFirstLogin) {
          setTimeout(() => setIsActive(true), 500);
        }
      } catch (error) {
        console.error("Error parsing login data:", error);
      }
    }
  }, [run, manualTrigger]);

  useEffect(() => {
    if (!isActive) return;

    const updatePosition = () => {
      const step = steps[currentStep];
      const targetElement = document.querySelector(step.target);

      if (tooltipRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        let top = 0;
        let left = 0;

        // Center body-targeted steps (welcome and completion)
        if (step.target === "body") {
          top = window.innerHeight / 2 - tooltipRect.height / 2;
          left = window.innerWidth / 2 - tooltipRect.width / 2;
          setTooltipPosition({ top, left });
          return;
        }

        if (targetElement) {
          const rect = targetElement.getBoundingClientRect();

          switch (step.placement) {
            case "bottom":
              top = rect.bottom + 16;
              left = rect.left + rect.width / 2 - tooltipRect.width / 2;
              break;
            case "top":
              top = rect.top - tooltipRect.height - 16;
              left = rect.left + rect.width / 2 - tooltipRect.width / 2;
              break;
            case "left":
              top = rect.top + rect.height / 2 - tooltipRect.height / 2;
              left = rect.left - tooltipRect.width - 16;
              break;
            case "right":
              top = rect.top + rect.height / 2 - tooltipRect.height / 2;
              left = rect.right + 16;
              break;
            default:
              top = window.innerHeight / 2 - tooltipRect.height / 2;
              left = window.innerWidth / 2 - tooltipRect.width / 2;
          }

          // Boundary checks to keep tooltip on screen
          left = Math.max(
            16,
            Math.min(left, window.innerWidth - tooltipRect.width - 16),
          );
          top = Math.max(
            16,
            Math.min(top, window.innerHeight - tooltipRect.height - 16),
          );

          setTooltipPosition({ top, left });

          // Scroll target into view
          targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    };

    updatePosition();
    // Delay to allow for DOM stabilization
    const timer = setTimeout(updatePosition, 100);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [currentStep, isActive]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setIsActive(false);
    localStorage.setItem("onboarding_completed", "true");
    if (onComplete) {
      onComplete();
    }
  };

  if (!isActive) return null;

  const step = steps[currentStep];
  const targetElement = document.querySelector(step.target);

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm transition-opacity duration-300" />

      {/* Spotlight */}
      {targetElement && step.target !== "body" && (
        <div
          className="fixed z-[9999] pointer-events-none transition-all duration-300"
          style={{
            top: targetElement.getBoundingClientRect().top - 8,
            left: targetElement.getBoundingClientRect().left - 8,
            width: targetElement.getBoundingClientRect().width + 16,
            height: targetElement.getBoundingClientRect().height + 16,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
            borderRadius: "8px",
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] bg-white rounded-xl shadow-2xl p-6 max-w-md animate-in fade-in zoom-in duration-300"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>

        {/* Content */}
        <div className="mb-6">
          {currentStep === 0 || currentStep === steps.length - 1 ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {step.title}
              </h2>
              <p className="text-gray-600">{step.content}</p>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {step.title}
              </h3>
              <p className="text-gray-600">{step.content}</p>
            </>
          )}
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#243677] transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors"
          >
            Skip Tour
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium text-sm transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-4 py-2 bg-[#243677] text-white rounded-lg font-medium text-sm hover:bg-[#1e2a5e] transition-colors"
            >
              {currentStep === steps.length - 1 ? "Finish" : "Next"}
              {currentStep < steps.length - 1 && (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingGuide;
